import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import MediaQuery from 'react-responsive';
import ENDPOINT from '../../constants/endpoints';
import {CONNECT_STATUS, POSITION_FLAG} from '../../constants/profile';
import ROUTE from '../../constants/route';
import LinkBuilder from '../../helpers/link-builder';
import useApi from '../../hooks/useApi';
import ProfileRecommendations from '../../scenes/CandidateProfile/components/ProfileRecommendations';
import Avatar from '../Avatar';
import BasicButton from '../BasicButton';
import BasicButtonGroup from '../BasicButtonGroup';
import BasicNavLinkButton from '../BasicNavLinkButton';
import BasicSelect from '../BasicSelect';

import './styles.css';
import BasicSlideToggle from '../BasicSlideToggle';
import EmailPreferencesPopup from '../EmailPreferencesPopup';
import LinkButton from '../LinkButton';
import ReadMore from '../ReadMore';

const ProfileHeader = props => {
  const {
    resumeHistory,
    data,
    actions = {},
    isAddReviewBtnHidden,
    isContactInformationHidden,
    showMailPreferences = false,
  } = props;

  const {
    FullName,
    PictureUrl,
    EmailAddress,
    ConnectStatus,
    ResumeUpdateData,
    ResumeUrl,
    EmailAddress2,
    PhoneNumber1,
    PhoneNumber2,
    Rated,
    InterviewQuestionsEmailSentDate,
    InterviewRequestSentDate,
    Lock,
    position: {
      PositionID,
      Approved,
      TitleName,
      DegreeName,
      SchoolName,
      UndergraduateSchoolName,
      Description,
      Recommendations,
      ReviewID,
    },
    experience,
  } = data;

  const {
    onPhotoEditHandler,
    onPhotoDeleteHandler,
    onResumeUploadHandler,
    onEditConfidential,
    onAddRecommendation,
    onEditProfile,
    onConnect,
    onDisconnect,
    onMessage,
    onRate,
    onAddFile,
    onApply,
    onTagProfile,
    onAddToShortlist,
    onDownloadResume,
    onAccept,
    onReject,
    onSendInterviewQuestions,
    onSendReview,
    onUpdateResume,
    onSendGAT,
    onSendJob,
    onLockProfile,
    onSchedule,
  } = actions;

  const api = useApi();
  const [scheduleUsersList, setScheduleUsersList] = useState([]);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [recruiterSelectValid, setRecruiterSelectValid] = useState(false);
  const [emailPreferencesPopupOpen, setEmailPreferencesPopupOpen] = useState(false);

  useEffect(() => {
    api.get(ENDPOINT.COLLECTION.INTERVIEW_REQUEST_USERS, {}, {
      handleSuccess: setScheduleUsersList,
    });
  }, []);

  const handleAddRecommendation = () => {
    onAddRecommendation(props.data.position);
  };

  const handleLockProfile = () => {
    onLockProfile(!Lock.State);
  };

  const userPicture = useMemo(
    () => LinkBuilder.getCandidatePictureSizeLink(PictureUrl, '160x160'),
    [PictureUrl]
  );

  const [
    isEducation,
    reviewLink,
  ] = useMemo(() => {
    const isEducation = data.position.flag === POSITION_FLAG.EDUCATION;

    const reviewLink =
      ReviewID || Approved === '0' || isAddReviewBtnHidden
        ? null
        : `${ROUTE.ADD_REVIEW_EMPLOYEE}?position=${PositionID}`;

    return [isEducation, reviewLink];
  }, [Approved, PositionID, ReviewID, data.position.flag, isAddReviewBtnHidden]);

  const onManageMailPreferences = useCallback(() => {
    setEmailPreferencesPopupOpen(true);
  }, []);

  const positionDisplay = useMemo(() => {
    const experiencePosition = experience?.length ? experience[0] : null;

    let role = TitleName;
    let title = DegreeName;

    const description = experiencePosition?.groups?.map(group => group.GroupName) || [];

    if (experiencePosition?.industries) {
      experiencePosition?.industries.forEach(industry => {
        description.push(industry.IndustryName);
      });
    }

    if (experiencePosition) {
      role = experiencePosition.position;
      title = experiencePosition.role;
    }

    const firmLink =
      experiencePosition && experiencePosition.firmId
        ? LinkBuilder.getFirmLink(experiencePosition.firmUrlSlug)
        : '#';

    return (
      <>
        <div className="profile-header__row">
          {!!role && (
            <span className="profile-header__role">{`${role} at`}</span>
          )}

          {!experiencePosition ? (
            <div className="profile-header__firm">
              <strong
                style={{
                  fontWeight: 400,
                  fontFamily: 'Noto Sans',
                  fontSize: '0.9286rem',
                }}
              >
                {SchoolName || UndergraduateSchoolName}
              </strong>
            </div>
          ) : (
            <div className="profile-header__firm">
              {experiencePosition.firmActive ? (
                <LinkButton
                  text={experiencePosition.firmName}
                  to={firmLink}
                  btnFontFamily="Noto Sans"
                  btnFontWeight="400"
                />
              ) : (
                <span>{experiencePosition.firmName}</span>
              )}
            </div>
          )}
        </div>
        {description.length > 0 && (
          <div className="profile-header__row">
            <span className="profile-header__text">
              {description.join(' / ')}
            </span>
          </div>
        )}
        {!!title && (
          <div className="profile-header__row">
            <span className="profile-header__title-name">{title}</span>
          </div>
        )}
        {!!experiencePosition && (
          <div className="profile-header__row">
            <span className="profile-header__text profile-header__text--small">
              {experiencePosition.city}
            </span>
          </div>
        )}
      </>
    );
  }, [DegreeName, SchoolName, TitleName, UndergraduateSchoolName, experience]);

  return (
    <div className="profile-header">
      <div className="container">
        <div className="profile-header__inner">
          <div className="profile-header__avatar">
            <div className="profile-header__avatar--inner">
              <Avatar size={160} name={FullName} image={userPicture} />
              <div className="profile-header__avatar-controls">
                {onPhotoEditHandler && (
                  <label
                    className="profile-header__avatar--label"
                    onClick={onPhotoEditHandler}
                  />
                )}
                {PictureUrl && onPhotoDeleteHandler && (
                  <label
                    className="profile-header__avatar--delete"
                    onClick={onPhotoDeleteHandler}
                  />
                )}
              </div>
            </div>

            <MediaQuery query="(min-width: 1200px)">
              <div className="profile-header__actions">
                {onConnect && CONNECT_STATUS.NOT_CONNECTED === ConnectStatus && (
                  <div className="profile-header__action">
                    <BasicButton
                      disabled={Lock && Lock.State}
                      btnWidth={200}
                      btnHeight={35}
                      btnFontSize="0.8571rem"
                      btnFontWeight={600}
                      btnText="Connect"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={onConnect}
                    />
                  </div>
                )}

                {onDisconnect && CONNECT_STATUS.CONNECTED === ConnectStatus && (
                  <div className="profile-header__action">
                    <BasicButton
                      disabled={Lock && Lock.State}
                      btnWidth={200}
                      btnHeight={35}
                      btnFontSize="0.8571rem"
                      btnFontWeight={600}
                      btnText="Disconnect"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={onDisconnect}
                    />
                  </div>
                )}

                {CONNECT_STATUS.REQUIRE_APPROVE === ConnectStatus && (
                  <div className="profile-header__action">
                    <BasicButton
                      disabled
                      btnWidth={200}
                      btnHeight={35}
                      btnFontSize="0.8571rem"
                      btnFontWeight={600}
                      btnText="Pending"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                    />
                  </div>
                )}

                {CONNECT_STATUS.REQUIRE_ACTION === ConnectStatus && (
                  <Fragment>
                    {onAccept && (
                      <div className="profile-header__action">
                        <BasicButton
                          disabled={Lock && Lock.State}
                          btnWidth={200}
                          btnHeight={35}
                          btnFontSize="0.8571rem"
                          btnFontWeight={600}
                          btnText="Accept"
                          btnArrow={false}
                          btnBg="#fff"
                          btnHoverBg="#f40"
                          controlFunc={onAccept}
                        />
                      </div>
                    )}
                    {onReject && (
                      <div className="profile-header__action">
                        <BasicButton
                          disabled={Lock && Lock.State}
                          btnWidth={200}
                          btnHeight={35}
                          btnFontSize="0.8571rem"
                          btnFontWeight={600}
                          btnText="Reject"
                          btnArrow={false}
                          btnBg="#fff"
                          btnHoverBg="#f40"
                          controlFunc={onReject}
                        />
                      </div>
                    )}
                  </Fragment>
                )}

                {onMessage && (
                  <div className="profile-header__action">
                    <BasicButton
                      disabled={Lock && Lock.State}
                      btnWidth={200}
                      btnHeight={35}
                      btnFontSize="0.8571rem"
                      btnFontWeight={600}
                      btnText="Message"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={onMessage}
                    />
                  </div>
                )}

                {onApply && (
                  <div className="profile-header__action">
                    <BasicButton
                      disabled={Lock && Lock.State}
                      btnWidth={200}
                      btnHeight={35}
                      btnFontSize="0.8571rem"
                      btnFontWeight={600}
                      btnText="Apply"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={onApply}
                    />
                  </div>
                )}
              </div>
            </MediaQuery>
          </div>

          <div className="profile-header__info">
            <div className={'profile-heder__main'}>
              <div className="profile-header__row profile-header__row--spread">
                <h2 className="profile-header__name">{FullName}</h2>

                {(onEditProfile || Lock) && (
                  <div className="profile-header-control-group profile-header-control--edit-profile">
                    {onEditProfile && (
                      <div className="profile-header-control-group__control">
                        <LinkButton
                          text="Edit Profile"
                          controlFunc={onEditProfile}
                        />
                      </div>
                    )}
                    {Lock && (
                      <>
                        <div className="profile-header--toggle">
                          <BasicSlideToggle
                            disabled={
                              Lock.hasOwnProperty('Readonly') && Lock.Readonly
                            }
                            name="Lock"
                            label={Lock.State ? 'Unlock' : 'Lock'}
                            checked={Lock.State}
                            onChange={handleLockProfile}
                          />
                        </div>
                        {Lock.State && (
                          <div className="profile-header--toggle-info">
                            {`Locked by ${Lock.Author || 'Admin'}`}
                            <MediaQuery query="(min-width: 1201px)">
                              <br />
                            </MediaQuery>
                            {`on ${Lock.Time}`}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {positionDisplay}
            </div>

            <div className="profile-header__row profile-header__row--spread profile-header__row--additional">
              <div
                className="profile-header__send-interview"
                style={{ margin: '15px 0' }}
              >
                {showMailPreferences && (
                  <div className={'profile-header__email-preferences'}>
                    <BasicButton
                      btnText="Pause emails"
                      btnWidth={150}
                      btnHeight={35}
                      btnFontSize="0.8571rem"
                      btnFontWeight={600}
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={onManageMailPreferences}
                    />
                    <EmailPreferencesPopup
                      candidateId={data.candidateId}
                      isOpen={emailPreferencesPopupOpen}
                      onClose={() => setEmailPreferencesPopupOpen(false)}
                    />
                  </div>
                )}
                {onSchedule && (
                  <div className={'profile-header__schedule-meeting'}>
                    <BasicButtonGroup>
                      <BasicSelect
                        isSearchable={false}
                        value={selectedRecruiter}
                        isValid={recruiterSelectValid}
                        placeholder="Select recruiter"
                        options={scheduleUsersList}
                        controlFunc={obj => {
                          setSelectedRecruiter(obj);
                          setRecruiterSelectValid(typeof obj !== 'undefined');
                        }}
                      />
                      <BasicButton
                        disabled={!recruiterSelectValid}
                        btnText="Schedule a meeting"
                        btnWidth={150}
                        btnHeight={35}
                        btnFontSize="0.8571rem"
                        btnFontWeight={600}
                        btnArrow={false}
                        btnBg="#fff"
                        btnHoverBg="#f40"
                        controlFunc={() => {
                          onSchedule(selectedRecruiter.value);
                        }}
                      />
                    </BasicButtonGroup>
                    {InterviewRequestSentDate && (
                      <div className="profile-header__text profile-header__text--small profile-header__text--right">
                        {`Sent: ${InterviewRequestSentDate}`}
                      </div>
                    )}
                  </div>
                )}
                {onSendInterviewQuestions && (
                  <>
                    <BasicButtonGroup>
                      <BasicButton
                        disabled={Lock && Lock.State}
                        btnText="Send Interview Questions"
                        btnWidth={150}
                        btnHeight={35}
                        btnFontSize="0.8571rem"
                        btnFontWeight={600}
                        btnArrow={false}
                        btnBg="#fff"
                        btnHoverBg="#f40"
                        controlFunc={onSendInterviewQuestions}
                      />
                    </BasicButtonGroup>

                    {InterviewQuestionsEmailSentDate && (
                      <div className="profile-header__text profile-header__text--small profile-header__text--right">
                        {`Sent: ${InterviewQuestionsEmailSentDate}`}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {Description && (
              <div className="profile-header__description">
                <ReadMore text={Description} readMoreCharacterLimit={320} />
              </div>
            )}

            {Recommendations && (
              <div className="profile-header__description">
                <ProfileRecommendations list={Recommendations} />
              </div>
            )}

            <div className="profile-header__buttons">
              <MediaQuery query="(max-width: 1199px)">
                <BasicButtonGroup>
                  {onConnect &&
                    CONNECT_STATUS.NOT_CONNECTED === ConnectStatus && (
                      <BasicButton
                        disabled={Lock && Lock.State}
                        btnWidth={200}
                        btnHeight={35}
                        btnFontSize="0.8571rem"
                        btnFontWeight={600}
                        btnText="Connect"
                        btnArrow={false}
                        btnBg="#fff"
                        btnHoverBg="#f40"
                        controlFunc={onConnect}
                      />
                    )}

                  {onDisconnect &&
                    CONNECT_STATUS.CONNECTED === ConnectStatus && (
                      <BasicButton
                        disabled={Lock && Lock.State}
                        btnWidth={200}
                        btnHeight={35}
                        btnFontSize="0.8571rem"
                        btnFontWeight={600}
                        btnText="Disconnect"
                        btnArrow={false}
                        btnBg="#fff"
                        btnHoverBg="#f40"
                        controlFunc={onDisconnect}
                      />
                    )}

                  {CONNECT_STATUS.REQUIRE_APPROVE === ConnectStatus && (
                    <BasicButton
                      disabled
                      btnWidth={200}
                      btnHeight={35}
                      btnFontSize="0.8571rem"
                      btnFontWeight={600}
                      btnText="Pending"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                    />
                  )}

                  {CONNECT_STATUS.REQUIRE_ACTION === ConnectStatus &&
                    onAccept && (
                      <BasicButton
                        disabled={Lock && Lock.State}
                        btnWidth={200}
                        btnHeight={35}
                        btnFontSize="0.8571rem"
                        btnFontWeight={600}
                        btnText="Accept"
                        btnArrow={false}
                        btnBg="#fff"
                        btnHoverBg="#f40"
                        controlFunc={onAccept}
                      />
                    )}

                  {CONNECT_STATUS.REQUIRE_ACTION === ConnectStatus &&
                    onReject && (
                      <BasicButton
                        disabled={Lock && Lock.State}
                        btnWidth={200}
                        btnHeight={35}
                        btnFontSize="0.8571rem"
                        btnFontWeight={600}
                        btnText="Reject"
                        btnArrow={false}
                        btnBg="#fff"
                        btnHoverBg="#f40"
                        controlFunc={onReject}
                      />
                    )}

                  {onMessage && (
                    <BasicButton
                      disabled={Lock && Lock.State}
                      btnWidth={200}
                      btnHeight={35}
                      btnFontSize="0.8571rem"
                      btnFontWeight={600}
                      btnText="Message"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={onMessage}
                    />
                  )}

                  {onApply && (
                    <BasicButton
                      disabled={Lock && Lock.State}
                      btnWidth={200}
                      btnHeight={35}
                      btnFontSize="0.8571rem"
                      btnFontWeight={600}
                      btnText="Apply"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={onApply}
                    />
                  )}
                </BasicButtonGroup>
              </MediaQuery>

              {onResumeUploadHandler ? (
                <MediaQuery query="(min-width: 1201px)">
                  <BasicButtonGroup>
                    <BasicButton
                      btnWidth={150}
                      btnHeight={35}
                      btnFontSize="0.8571rem"
                      btnFontWeight={600}
                      btnText="Edit Resume"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={onResumeUploadHandler}
                    />
                  </BasicButtonGroup>
                </MediaQuery>
              ) : null}

              <BasicButtonGroup>
                {!isEducation && reviewLink && (
                  <BasicNavLinkButton
                    btnWidth={150}
                    btnHeight={35}
                    btnFontSize={12}
                    btnFontWeight={600}
                    btnText="Add Review"
                    btnArrow={false}
                    btnBg="#fff"
                    btnHoverBg="#f40"
                    to={reviewLink}
                  />
                )}

                {onAddRecommendation && (
                  <BasicButton
                    btnWidth={150}
                    btnHeight={35}
                    btnFontSize="0.8571rem"
                    btnFontWeight={600}
                    btnText="Ask for Recommendation"
                    btnArrow={false}
                    btnBg="#fff"
                    btnHoverBg="#f40"
                    controlFunc={handleAddRecommendation}
                  />
                )}

                {onAddFile && (
                  <BasicButton
                    btnWidth={150}
                    btnHeight={35}
                    btnFontSize="0.8571rem"
                    btnFontWeight={600}
                    btnText="Add File"
                    btnArrow={false}
                    btnBg="#fff"
                    btnHoverBg="#f40"
                    controlFunc={onAddFile}
                  />
                )}

                {onRate && (
                  <BasicButton
                    btnWidth={150}
                    btnHeight={35}
                    btnFontSize="0.8571rem"
                    btnFontWeight={600}
                    btnText={Rated ? 'Change Rating' : 'Rate'}
                    btnArrow={false}
                    btnBg="#fff"
                    btnHoverBg="#f40"
                    controlFunc={onRate}
                  />
                )}

                {onTagProfile && (
                  <BasicButton
                    btnWidth={150}
                    btnHeight={35}
                    btnFontSize="0.8571rem"
                    btnFontWeight={600}
                    btnText="Tag Profile"
                    btnArrow={false}
                    btnBg="#fff"
                    btnHoverBg="#f40"
                    controlFunc={onTagProfile}
                  />
                )}

                {onAddToShortlist && (
                  <BasicButton
                    btnWidth={150}
                    btnHeight={35}
                    btnFontSize="0.8571rem"
                    btnFontWeight={600}
                    btnText="Shortlist"
                    btnArrow={false}
                    btnBg="#fff"
                    btnHoverBg="#f40"
                    controlFunc={onAddToShortlist}
                  />
                )}
                {onUpdateResume && (
                  <MediaQuery query="(max-width: 1200px)">
                    <BasicButton
                      disabled={Lock && Lock.State}
                      btnWidth={150}
                      btnHeight={35}
                      btnFontSize="0.8571rem"
                      btnFontWeight={600}
                      btnText="Update Resume"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={onUpdateResume}
                    />
                  </MediaQuery>
                )}
                {onSendJob && (
                  <MediaQuery query="(max-width: 1200px)">
                    <BasicButton
                      disabled={Lock && Lock.State}
                      btnWidth={150}
                      btnHeight={35}
                      btnFontSize="0.8571rem"
                      btnFontWeight={600}
                      btnText="Send Job"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={onSendJob}
                    />
                  </MediaQuery>
                )}
                {onSendGAT && (
                  <MediaQuery query="(max-width: 1200px)">
                    <BasicButton
                      disabled={Lock && Lock.State}
                      btnWidth={150}
                      btnHeight={35}
                      btnFontSize="0.8571rem"
                      btnFontWeight={600}
                      btnText="Send GAT"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={onSendGAT}
                    />
                  </MediaQuery>
                )}
                {onSendReview && (
                  <MediaQuery query="(max-width: 1200px)">
                    <BasicButton
                      disabled={Lock && Lock.State}
                      btnWidth={150}
                      btnHeight={35}
                      btnFontSize="0.8571rem"
                      btnFontWeight={600}
                      btnText="Send Review"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={onSendReview}
                    />
                  </MediaQuery>
                )}
              </BasicButtonGroup>
            </div>
            <div className="profile-header__buttons profile-header__buttons--second-row">
              <MediaQuery query="(min-width: 1201px)">
                <BasicButtonGroup
                  groupStyles={{ justifyContent: 'flex-start', width: '100%' }}
                >
                  {onUpdateResume && (
                    <BasicButton
                      disabled={Lock && Lock.State}
                      btnWidth={150}
                      btnHeight={35}
                      btnFontSize="0.8571rem"
                      btnFontWeight={600}
                      btnText="Update Resume"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={onUpdateResume}
                    />
                  )}
                  {onSendJob && (
                    <BasicButton
                      disabled={Lock && Lock.State}
                      btnWidth={150}
                      btnHeight={35}
                      btnFontSize="0.8571rem"
                      btnFontWeight={600}
                      btnText="Send Job"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={onSendJob}
                    />
                  )}
                  {onSendGAT && (
                    <BasicButton
                      disabled={Lock && Lock.State}
                      btnWidth={150}
                      btnHeight={35}
                      btnFontSize="0.8571rem"
                      btnFontWeight={600}
                      btnText="Send GAT"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={onSendGAT}
                    />
                  )}
                  {onSendReview && (
                    <BasicButton
                      disabled={Lock && Lock.State}
                      btnWidth={150}
                      btnHeight={35}
                      btnFontSize="0.8571rem"
                      btnFontWeight={600}
                      btnText="Send Review"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={onSendReview}
                    />
                  )}
                </BasicButtonGroup>
              </MediaQuery>
            </div>
          </div>

          {!isContactInformationHidden ? (
            <div className="profile-header__contact-info">
              <h4 className="profile-header__subtitle">Contact Information</h4>

              {EmailAddress && (
                <div className="profile-header__contact">
                  <LinkButton
                    text={EmailAddress}
                    link={`mailto:${EmailAddress}`}
                    btnFontFamily="Noto Sans, sans-serif"
                    btnFontWeight="400"
                  />
                </div>
              )}

              {EmailAddress2 && (
                <div className="profile-header__contact">
                  <LinkButton
                    text={EmailAddress2}
                    link={`mailto:${EmailAddress2}`}
                    btnFontFamily="Noto Sans, sans-serif"
                    btnFontWeight="400"
                  />
                </div>
              )}

              {PhoneNumber1 && (
                <div className="profile-header__contact">
                  <span className="profile-header__phone">{PhoneNumber1}</span>
                </div>
              )}

              {PhoneNumber2 && (
                <div className="profile-header__contact">
                  <span className="profile-header__phone">{PhoneNumber2}</span>
                </div>
              )}

              {onEditConfidential && (
                <div className="profile-header__contact">
                  <LinkButton
                    btnFontSize="0.9286rem"
                    text="Edit"
                    controlFunc={onEditConfidential}
                  />
                </div>
              )}

              {resumeHistory}

              <div className="profile-header__contact-action">
                {ResumeUpdateData && (
                  <p className="profile-header__text profile-header__text--small">{`Resume uploaded on ${ResumeUpdateData}`}</p>
                )}
                <BasicButton
                  disabled={!ResumeUrl}
                  btnWidth={150}
                  btnHeight={35}
                  btnFontSize="0.8571rem"
                  btnFontWeight={600}
                  btnText="Download Resume"
                  btnArrow={false}
                  btnBg="#fff"
                  btnHoverBg="#f40"
                  controlFunc={onDownloadResume}
                />

                {onResumeUploadHandler ? (
                  <MediaQuery query="(max-width: 1200px)">
                    <div style={{ marginTop: '4px' }}>
                      <BasicButton
                        btnWidth={150}
                        btnHeight={35}
                        btnFontSize="0.8571rem"
                        btnFontWeight={600}
                        btnText="Edit Resume"
                        btnArrow={false}
                        btnBg="#fff"
                        btnHoverBg="#f40"
                        controlFunc={onResumeUploadHandler}
                      />
                    </div>
                  </MediaQuery>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="profile-header__contact-info">&nbsp;</div>
          )}
        </div>
      </div>
    </div>
  );
};

ProfileHeader.propTypes = {
  data: PropTypes.object.isRequired,
  actions: PropTypes.object,

  isAddReviewBtnHidden: PropTypes.bool,
  isContactInformationHidden: PropTypes.bool,
  isFirmRatingHidden: PropTypes.bool,

  resumeHistory: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

export default ProfileHeader;
